import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ATID } from "@/lib/adminTestIds";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import {
  Check, X, Sparkles, Loader2, Image as ImageIcon, ExternalLink, Zap, Send,
} from "lucide-react";
import { formatDateTime } from "@/lib/date";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATUS_LABELS = {
  detected: { label: "Detectada", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  date_unconfirmed: { label: "Sin fecha", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  date_rejected: { label: "Fuera de fecha (revisar)", cls: "bg-yellow-50 text-yellow-800 border-yellow-300" },
  duplicate: { label: "Duplicada", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  pending_processing: { label: "Pendiente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  processing: { label: "Procesando", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  ready_for_review: { label: "Lista para revisión", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  approved: { label: "Aprobada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rechazada", cls: "bg-textgray/10 text-textgray border-textgray/30" },
  published: { label: "Publicada", cls: "bg-teal-50 text-teal-700 border-teal-200" },
  ai_error: { label: "Error IA", cls: "bg-red-50 text-red-700 border-red-200" },
  extraction_error: { label: "Error extracción", cls: "bg-red-50 text-red-700 border-red-200" },
};

const PROCESSABLE = new Set(["detected", "date_unconfirmed", "date_rejected", "ai_error", "extraction_error", "ready_for_review"]);

export default function AutomationInboxTab({ sources, counts, onChanged }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", source_id: "", q: "" });
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState({});
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchPublishRunning, setBatchPublishRunning] = useState(false);
  const [batchDeleteRunning, setBatchDeleteRunning] = useState(false);
  const [batchSummary, setBatchSummary] = useState(null); // { published, sent_to_review, errors, skipped, items }

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (filters.status) params.status = filters.status;
      if (filters.source_id) params.source_id = filters.source_id;
      if (filters.q) params.q = filters.q;
      const data = await api.adminAutoListImported(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("No se pudo cargar la bandeja");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { limit: 50 };
        if (filters.status) params.status = filters.status;
        if (filters.source_id) params.source_id = filters.source_id;
        if (filters.q) params.q = filters.q;
        const data = await api.adminAutoListImported(params);
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (e) {
        if (!cancelled) toast.error("No se pudo cargar la bandeja");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filters.status, filters.source_id]);

  const selectedIds = useMemo(
    () => items.filter((it) => selected[it.id] && PROCESSABLE.has(it.status)).map((it) => it.id),
    [items, selected]
  );
  const processableCount = useMemo(
    () => items.filter((it) => PROCESSABLE.has(it.status)).length,
    [items]
  );

  const toggleAll = (checked) => {
    if (!checked) return setSelected({});
    const next = {};
    items.filter((it) => PROCESSABLE.has(it.status)).forEach((it) => { next[it.id] = true; });
    setSelected(next);
  };

  const deleteOne = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.original_title || item.generated_title || "esta noticia"}"?`)) return;
    setBusyId(item.id);
    try {
      await api.adminAutoDeleteImported(item.id);
      toast.success("Noticia eliminada");
      await load();
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "No se pudo eliminar"));
    } finally { setBusyId(null); }
  };

  const runBatchDelete = async () => {
    const allSelectedIds = Object.keys(selected).filter((id) => selected[id]);
    if (!allSelectedIds.length) {
      toast.error("Seleccioná al menos una noticia");
      return;
    }
    if (!window.confirm(`¿Eliminar ${allSelectedIds.length} noticia(s) seleccionada(s)? Esta acción no se puede deshacer.`)) return;
    setBatchDeleteRunning(true);
    try {
      const r = await api.adminAutoBatchDelete({ item_ids: allSelectedIds });
      toast.success(`Eliminadas ${r.deleted_count} de ${r.requested} noticia(s)`);
      setSelected({});
      await load();
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Error al eliminar en lote"));
    } finally {
      setBatchDeleteRunning(false);
    }
  };

  const changeStatus = async (item, newStatus, verb) => {
    setBusyId(item.id);
    try {
      await api.adminAutoUpdateImported(item.id, { status: newStatus });
      toast.success(`Noticia ${verb}`);
      await load();
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo actualizar");
    } finally {
      setBusyId(null);
    }
  };

  const processOne = async (item) => {
    setBusyId(item.id);
    try {
      await api.adminAutoProcessAi(item.id);
      toast.success("Procesada con IA — pasó a revisión");
      await load();
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "No se pudo procesar con IA");
      toast.error(msg);
    } finally { setBusyId(null); }
  };

  const runBatch = async () => {
    const ids = selectedIds;
    setBatchRunning(true);
    try {
      const r = await api.adminAutoBatchProcess({ item_ids: ids.length ? ids : undefined });
      toast.success(`Lote finalizado: ${r.processed} procesadas · ${r.errors} errores · ${r.skipped} omitidas`);
      setSelected({});
      await load();
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Error en el procesamiento en lote"));
    } finally {
      setBatchRunning(false);
    }
  };

  const runBatchPublish = async (portalOnly = false) => {
    const ids = selectedIds;
    if (!ids.length) {
      toast.error("Seleccioná al menos una noticia");
      return;
    }
    if (portalOnly) {
      if (!window.confirm("Las noticias seleccionadas serán procesadas con el reglamento editorial de Sur Primicia y publicadas únicamente en el portal.\n\nNo se enviarán a Facebook ni Instagram.")) return;
    }
    setBatchPublishRunning(true);
    try {
      const r = await api.adminAutoBatchProcessAndPublish({ item_ids: ids, portal_only: portalOnly });
      setBatchSummary(r);
      const parts = [
        r.published ? `${r.published} publicada(s)${portalOnly ? " (solo portal)" : ""}` : null,
        r.sent_to_review ? `${r.sent_to_review} a revisión` : null,
        r.errors ? `${r.errors} con error` : null,
        r.skipped ? `${r.skipped} omitida(s)` : null,
      ].filter(Boolean);
      toast.success(`Lote finalizado — ${parts.join(" · ")}`);
      setSelected({});
      await load();
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Error en procesamiento+publicación"));
    } finally {
      setBatchPublishRunning(false);
    }
  };

  const empty = !loading && items.length === 0;

  const badgeFor = (status) => {
    const s = STATUS_LABELS[status] || STATUS_LABELS.detected;
    return <Badge variant="outline" className={s.cls}>{s.label}</Badge>;
  };

  return (
    <section>
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-textgray mb-1">Estado</div>
          <Select
            value={filters.status || "all"}
            onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}
          >
            <SelectTrigger data-testid={ATID.automationInboxFilterStatus} className="min-w-[200px]"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label} {counts?.[k] ? `(${counts[k]})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-textgray mb-1">Fuente</div>
          <Select
            value={filters.source_id || "all"}
            onValueChange={(v) => setFilters((f) => ({ ...f, source_id: v === "all" ? "" : v }))}
          >
            <SelectTrigger data-testid={ATID.automationInboxFilterSource} className="min-w-[220px]"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las fuentes</SelectItem>
              {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] uppercase tracking-widest text-textgray mb-1">Buscar</div>
          <Input
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Título o URL"
          />
        </div>
        <Button variant="outline" onClick={load}>Filtrar</Button>
      </div>

      {/* Batch toolbar */}
      <div className="flex items-center justify-between bg-white border border-softblue px-3 py-2 mb-3 flex-wrap gap-2">
        <label className="flex items-center gap-2 text-xs text-textgray">
          <Checkbox
            data-testid={ATID.automationInboxSelectAll}
            checked={items.length > 0 && Object.keys(selected).filter((id) => selected[id]).length === items.length}
            onCheckedChange={(v) => {
              if (!v) return setSelected({});
              const next = {};
              items.forEach((it) => { next[it.id] = true; });
              setSelected(next);
            }}
          />
          Seleccionar todas ({items.length})
        </label>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-textgray">
            {Object.keys(selected).filter((id) => selected[id]).length > 0
              ? `${Object.keys(selected).filter((id) => selected[id]).length} seleccionada(s)`
              : "sin selección"}
          </span>
          <Button
            onClick={runBatch}
            disabled={batchRunning || batchPublishRunning || batchDeleteRunning || processableCount === 0}
            data-testid={ATID.automationInboxBatchProcess}
            variant="outline"
            className="gap-2"
          >
            {batchRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Procesar con IA
          </Button>
          <Button
            onClick={() => runBatchPublish(false)}
            disabled={batchRunning || batchPublishRunning || batchDeleteRunning || selectedIds.length === 0}
            data-testid="automation-inbox-batch-process-and-publish"
            className="gap-2 bg-turquoise hover:bg-turquoise/90"
          >
            {batchPublishRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Procesar + publicar en lote
          </Button>
          <Button
            onClick={() => runBatchPublish(true)}
            disabled={batchRunning || batchPublishRunning || batchDeleteRunning || selectedIds.length === 0}
            data-testid="automation-inbox-batch-process-and-publish-portal-only"
            variant="outline"
            className="gap-2 border-turquoise/50 text-turquoise hover:bg-turquoise/10"
          >
            {batchPublishRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Procesar + publicar solo portal
          </Button>
          <Button
            onClick={runBatchDelete}
            disabled={batchRunning || batchPublishRunning || batchDeleteRunning || Object.keys(selected).filter((id) => selected[id]).length === 0}
            data-testid="automation-inbox-batch-delete"
            variant="outline"
            className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
          >
            {batchDeleteRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            Eliminar seleccionadas
          </Button>
        </div>
      </div>

      {empty ? (
        <div className="bg-white border border-softblue p-10 text-center">
          <p className="text-blueblack font-semibold">No hay noticias detectadas todavía.</p>
          <p className="text-sm text-textgray mt-1 max-w-lg mx-auto">
            Activá alguna fuente y usá <em>“Buscar noticias ahora”</em> arriba a la derecha
            —o esperá al próximo ciclo del scheduler— para que aparezcan aquí.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {loading && (
            <div className="text-center text-textgray py-6">
              <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Cargando…
            </div>
          )}
          {items.map((it) => (
            <article
              key={it.id}
              data-testid={ATID.automationInboxRow(it.id)}
              className="bg-white border border-softblue p-4 flex gap-4 items-start"
            >
              <div className="pt-1">
                <Checkbox
                  data-testid={ATID.automationInboxCheckbox(it.id)}
                  checked={!!selected[it.id]}
                  onCheckedChange={(v) => setSelected((s) => ({ ...s, [it.id]: !!v }))}
                />
              </div>
              <div className="w-28 h-20 bg-lightgray flex-shrink-0 overflow-hidden">
                {it.generated_image_url || it.original_image_url ? (
                  <img
                    src={it.generated_image_url || it.original_image_url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-textgray">
                    <ImageIcon className="w-5 h-5" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  {badgeFor(it.status)}
                  {it.error_code && (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 uppercase text-[10px]">
                      {it.error_code}
                    </Badge>
                  )}
                  <span className="text-[10px] uppercase tracking-widest text-textgray">
                    Detectada · {formatDateTime(it.detected_at)}
                  </span>
                </div>
                <h3 className="font-serif text-base font-bold text-blueblack line-clamp-2">
                  {it.generated_title || it.original_title || "Sin título"}
                </h3>
                <div className="text-xs text-textgray mt-1">
                  {(sources.find((s) => s.id === it.source_id)?.name) || "Fuente desconocida"}
                  {it.original_published_at ? ` · publicada ${formatDateTime(it.original_published_at)}` : ""}
                </div>
                {it.source_url && (
                  <a href={it.source_url} target="_blank" rel="noreferrer" className="text-xs text-turquoise hover:underline break-all inline-flex items-center gap-1 mt-1">
                    <ExternalLink className="w-3 h-3" /> Abrir original
                  </a>
                )}
                {it.error_message && (
                  <p className="text-xs text-red-600 mt-1">Error: {it.error_message}</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <Button
                  size="sm"
                  onClick={() => processOne(it)}
                  disabled={!PROCESSABLE.has(it.status) || busyId === it.id}
                  data-testid={ATID.automationInboxRowProcessAi(it.id)}
                  className="gap-1 bg-turquoise hover:bg-turquoise/90"
                >
                  {busyId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Procesar con IA
                </Button>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === it.id || ["approved", "published"].includes(it.status)}
                    onClick={() => changeStatus(it, "approved", "aprobada")}
                    data-testid={ATID.automationInboxApprove(it.id)}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Aprobar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === it.id}
                    onClick={() => deleteOne(it)}
                    data-testid={ATID.automationInboxReject(it.id)}
                    className="border-red-200 text-red-700 hover:bg-red-50"
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Eliminar
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {!empty && (
        <div className="text-xs text-textgray mt-3">{total} elementos totales</div>
      )}

      {/* Batch summary dialog */}
      <Dialog open={!!batchSummary} onOpenChange={(o) => !o && setBatchSummary(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="batch-publish-summary">
          <DialogHeader>
            <DialogTitle>Resumen del lote</DialogTitle>
          </DialogHeader>
          {batchSummary && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-emerald-50 border border-emerald-200 p-3">
                  <div className="text-2xl font-black text-emerald-700">{batchSummary.published || 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-textgray">Publicadas</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-3">
                  <div className="text-2xl font-black text-amber-700">{batchSummary.sent_to_review || 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-textgray">A revisión</div>
                </div>
                <div className="bg-red-50 border border-red-200 p-3">
                  <div className="text-2xl font-black text-red-700">{batchSummary.errors || 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-textgray">Errores</div>
                </div>
                <div className="bg-lightgray border border-softblue p-3">
                  <div className="text-2xl font-black text-textgray">{batchSummary.skipped || 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-textgray">Omitidas</div>
                </div>
              </div>
              <div className="border-t border-softblue pt-3 space-y-1.5">
                {(batchSummary.items || []).map((r, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs border border-softblue px-2 py-1.5">
                    <Badge variant="outline" className={
                      r.result === "published" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      r.result === "review" ? "bg-amber-50 text-amber-700 border-amber-200" :
                      r.result === "error" ? "bg-red-50 text-red-700 border-red-200" :
                      "bg-lightgray text-textgray border-softblue"
                    }>
                      {r.result}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-blueblack line-clamp-1">{r.title || r.id}</div>
                      {(r.reason || r.detail) && (
                        <div className="text-[11px] text-textgray line-clamp-2">
                          {r.reason ? `${r.reason} — ` : ""}{r.detail || ""}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
